import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Card, Stack, Typography, TextField, Button, Alert, CircularProgress, Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import QRCode from "qrcode";
import {
  requestOtp, verifyOtp, mfaStatus, mfaEnroll, mfaConfirm, mfaVerify,
  getAuthConfig, cognitoLogin, cognitoSetNewPassword, type AuthConfig,
} from "../services/auth";
import { BRAND } from "../styles/theme";

// coredb (prd): email → code → enroll|totp.
// cognito (sandbox/qa): password → [newpass] → enroll|totp. Ver docs/sandbox-cognito-auth.md.
type Step = "email" | "code" | "password" | "newpass" | "enroll" | "totp";

export default function Login() {
  const [step, setStep]   = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode]   = useState("");
  // 1er factor según el stage (GET /mfa/auth-config). null = cargando.
  const [authCfg, setAuthCfg]   = useState<AuthConfig | null>(null);
  const [password, setPassword] = useState("");
  const [newPass, setNewPass]   = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [cognitoSession, setCognitoSession] = useState("");
  const [totp, setTotp]   = useState("");
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState<string | null>(null);
  // Enrolamiento: otpauth:// URI (para el QR) + secreto base32 (carga manual).
  const [otpauthUri, setOtpauthUri] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [qrDataUrl, setQrDataUrl]   = useState("");
  const nav = useNavigate();

  useEffect(() => {
    getAuthConfig().then((cfg) => {
      setAuthCfg(cfg);
      if (cfg.mode === "cognito") setStep("password");
    });
  }, []);

  useEffect(() => {
    if (!otpauthUri) { setQrDataUrl(""); return; }
    QRCode.toDataURL(otpauthUri, { width: 220, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setErr("No se pudo generar el QR — usá el código manual."));
  }, [otpauthUri]);

  async function onRequest() {
    setErr(null); setBusy(true);
    try {
      await requestOtp(email);
      setStep("code");
    } catch (e: any) {
      setErr(e.message ?? "No pudimos enviarte el código");
    } finally { setBusy(false); }
  }

  // Con el 1er factor ya guardado: consulta el estado TOTP y sigue a enroll o totp.
  async function goSecondFactor() {
    const { enrolled } = await mfaStatus();
    if (enrolled) {
      setStep("totp");
    } else {
      const { otpauthUri: uri, secret } = await mfaEnroll();
      setOtpauthUri(uri); setTotpSecret(secret);
      setStep("enroll");
    }
    setTotp("");
  }

  async function onVerify() {
    setErr(null); setBusy(true);
    try {
      await verifyOtp(email, code);
      await goSecondFactor();
    } catch (e: any) {
      setErr(e.message ?? "Código inválido");
    } finally { setBusy(false); }
  }

  async function onCognitoLogin() {
    setErr(null); setBusy(true);
    try {
      const r = await cognitoLogin(email, password);
      if ("challenge" in r) {
        setCognitoSession(r.session);
        setNewPass(""); setNewPass2("");
        setStep("newpass");
        return;
      }
      await goSecondFactor();
    } catch (e: any) {
      setErr(e.message ?? "No se pudo iniciar sesión");
    } finally { setBusy(false); }
  }

  const newPassValid = newPass.length >= 12 && /[A-Z]/.test(newPass) && /[a-z]/.test(newPass) && /\d/.test(newPass);

  async function onCognitoNewPassword() {
    setErr(null); setBusy(true);
    try {
      const r = await cognitoSetNewPassword(email, cognitoSession, newPass);
      if ("challenge" in r) { setCognitoSession(r.session); setErr("Cognito volvió a pedir contraseña nueva."); return; }
      setPassword("");
      await goSecondFactor();
    } catch (e: any) {
      setErr(e.message ?? "No se pudo cambiar la contraseña");
    } finally { setBusy(false); }
  }

  async function onTotp() {
    setErr(null); setBusy(true);
    try {
      if (step === "enroll") await mfaConfirm(totp);
      else await mfaVerify(totp);
      nav("/", { replace: true });   // CP-482: el inicio es el Dashboard
    } catch (e: any) {
      setErr(e.message ?? "Código inválido");
    } finally { setBusy(false); }
  }

  const totpInput = (
    <TextField
      label="Código de 6 dígitos"
      value={totp}
      onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
      onKeyDown={(e) => { if (e.key === "Enter" && totp.length === 6 && !busy) onTotp(); }}
      autoFocus
      inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
    />
  );

  const totpSubmit = (
    <Button
      variant="contained" color="primary" size="large"
      disabled={totp.length !== 6 || busy}
      onClick={onTotp}
      startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
      sx={{ minWidth: 200 }}
    >
      {busy ? "Verificando..." : step === "enroll" ? "Activar y entrar" : "Entrar"}
    </Button>
  );

  return (
    <Box sx={{ minHeight: "100vh", background: "#fff" }}>
      {/* Violet banner header */}
      <Box sx={{
        background: BRAND.primary,
        color:      "#fff",
        pt:         10, pb: 18,
        textAlign:  "center",
      }}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56 }} />
          <Typography variant="h3" sx={{ fontWeight: 700 }}>Web Admin v2</Typography>
        </Stack>
      </Box>

      {/* Card shifted up to overlap the banner */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: -10, px: 2 }}>
        <Card sx={{ width: "100%", maxWidth: 680, p: { xs: 3, md: 5 }, borderRadius: 3, boxShadow: "0 20px 40px rgba(0,0,0,0.08)" }}>
          <Stack alignItems="center" mb={4}>
            <Typography sx={{ fontWeight: 700, fontSize: 18, borderBottom: `3px solid ${BRAND.primary}`, pb: 1, px: 2 }}>
              Log In
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Welcome!</Typography>
            {authCfg?.mode === "cognito" && (
              <Chip size="small" variant="outlined" label="Ambiente no productivo · auth Cognito"
                    sx={{ fontSize: 11, color: BRAND.primary, borderColor: BRAND.primary }} />
            )}
          </Stack>

          {authCfg === null && (
            <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>
          )}

          {authCfg !== null && step === "password" && (
            <>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Ingresá tu email y contraseña. Este ambiente no usa el CoreDB: las pantallas que
                dependen de producción no van a funcionar.
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoFocus
                  inputProps={{ autoComplete: "username" }}
                />
                <TextField
                  label="Contraseña" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && email.includes("@") && password && !busy) onCognitoLogin(); }}
                  inputProps={{ autoComplete: "current-password" }}
                />
              </Stack>
              {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
              <Stack alignItems="center" mt={3}>
                <Button
                  variant="contained" color="primary" size="large"
                  disabled={!email.includes("@") || !password || busy}
                  onClick={onCognitoLogin}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ minWidth: 240 }}
                >
                  {busy ? "Verificando..." : "Ingresar"}
                </Button>
              </Stack>
            </>
          )}

          {authCfg !== null && step === "newpass" && (
            <>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Es tu primer ingreso: elegí tu contraseña. Mínimo 12 caracteres, con mayúsculas,
                minúsculas y números.
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Nueva contraseña" type="password" value={newPass} autoFocus
                  onChange={(e) => setNewPass(e.target.value)}
                  error={newPass !== "" && !newPassValid}
                  helperText={newPass !== "" && !newPassValid ? "No cumple la política" : " "}
                  inputProps={{ autoComplete: "new-password" }}
                />
                <TextField
                  label="Repetí la contraseña" type="password" value={newPass2}
                  onChange={(e) => setNewPass2(e.target.value)}
                  error={newPass2 !== "" && newPass2 !== newPass}
                  helperText={newPass2 !== "" && newPass2 !== newPass ? "No coincide" : " "}
                  onKeyDown={(e) => { if (e.key === "Enter" && newPassValid && newPass === newPass2 && !busy) onCognitoNewPassword(); }}
                  inputProps={{ autoComplete: "new-password" }}
                />
              </Stack>
              {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
              <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
                <Button variant="text" onClick={() => { setStep("password"); setErr(null); }}>
                  Volver
                </Button>
                <Button
                  variant="contained" color="primary" size="large"
                  disabled={!newPassValid || newPass !== newPass2 || busy}
                  onClick={onCognitoNewPassword}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ minWidth: 200 }}
                >
                  {busy ? "Guardando..." : "Guardar y continuar"}
                </Button>
              </Stack>
            </>
          )}

          {authCfg !== null && step === "email" && (
            <>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Please enter your email so we can send you the access code
              </Typography>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && email.includes("@") && !busy) onRequest(); }}
                autoFocus
                inputProps={{ autoComplete: "email" }}
              />
              {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
              <Stack alignItems="center" mt={3}>
                <Button
                  variant="contained" color="primary" size="large"
                  disabled={!email.includes("@") || busy}
                  onClick={onRequest}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ minWidth: 240 }}
                >
                  {busy ? "Sending..." : "Receive code"}
                </Button>
              </Stack>
            </>
          )}

          {authCfg !== null && step === "code" && (
            <>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Ingresá el código que enviamos a <b>{email}</b>
              </Typography>
              <TextField
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value.trim())}
                onKeyDown={(e) => { if (e.key === "Enter" && code.length >= 4 && !busy) onVerify(); }}
                autoFocus
                inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
              />
              {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
              <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
                <Button variant="text" onClick={() => { setStep("email"); setErr(null); }}>
                  Cambiar email
                </Button>
                <Button
                  variant="contained" color="primary" size="large"
                  disabled={code.length < 4 || busy}
                  onClick={onVerify}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ minWidth: 200 }}
                >
                  {busy ? "Checking..." : "Log in"}
                </Button>
              </Stack>
            </>
          )}

          {step === "enroll" && (
            <>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Configurá el segundo factor: escaneá el QR con <b>Google Authenticator</b> o{" "}
                <b>Microsoft Authenticator</b> y después ingresá el código de 6 dígitos que muestra la app.
              </Typography>
              <Stack alignItems="center" spacing={2} sx={{ mb: 2 }}>
                {qrDataUrl
                  ? <img src={qrDataUrl} alt="QR de enrolamiento TOTP" width={220} height={220} />
                  : <CircularProgress size={32} />}
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                  ¿No podés escanear? Cargá esta clave manualmente:<br />
                  <Box component="code" sx={{ fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", userSelect: "all" }}>
                    {totpSecret}
                  </Box>
                </Typography>
                {totpInput}
              </Stack>
              {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
              <Stack alignItems="center">{totpSubmit}</Stack>
            </>
          )}

          {step === "totp" && (
            <>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Ingresá el código de 6 dígitos de tu app de autenticación (Google/Microsoft Authenticator).
              </Typography>
              <Stack alignItems="center" spacing={2}>
                {totpInput}
              </Stack>
              {err && <Alert severity="error" sx={{ mt: 2, mb: 1 }}>{err}</Alert>}
              <Stack alignItems="center" mt={2}>{totpSubmit}</Stack>
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
}
